import React, { useCallback, useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { Chart as ChartJS } from 'chart.js';
import { Line } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';
import type { KpiDrilldownSpec } from '../kpiDashboardApi';

const LINE_BLUE = '#2F5597';

export interface KpiRequestsWeeklyLineChartProps {
  volumeByWeek: { weekLabel: string; weekStart: string; incident: number; request: number }[];
  onDrill?: (spec: KpiDrilldownSpec) => void;
}

const KpiRequestsWeeklyLineChart: React.FC<KpiRequestsWeeklyLineChartProps> = ({ volumeByWeek, onDrill }) => {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const lineLabels = volumeByWeek.map((w) => w.weekLabel);
  const requestData = volumeByWeek.map((w) => w.request);
  const maxVol = Math.max(0, ...requestData, 1);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const chart = chartRef.current;
      if (!chart || !onDrill) return;
      const els = chartElementAtEvent(chart, event);
      const el = els[0];
      if (el == null) return;
      const week = volumeByWeek[el.index];
      if (!week || week.request <= 0) return;
      onDrill({
        kind: 'heatmap_volume_week',
        weekStart: week.weekStart,
        series: 'SERVICE_REQUEST',
      });
    },
    [onDrill, volumeByWeek]
  );

  return (
    <KpiChartPanel title="Requests By Week" className="h-full w-full" flexFill>
      <div className="relative min-h-[280px] w-full flex-1 px-1">
        <Line
          ref={chartRef}
          onClick={onDrill ? handleClick : undefined}
          data={{
            labels: lineLabels,
            datasets: [
              {
                label: 'Requests',
                data: requestData,
                borderColor: LINE_BLUE,
                backgroundColor: 'rgba(47, 85, 151, 0.12)',
                fill: true,
                tension: 0.35,
                pointRadius: 4,
                pointHoverRadius: 7,
                pointBackgroundColor: '#fff',
                pointBorderColor: LINE_BLUE,
                pointBorderWidth: 2,
                borderWidth: 2,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  clip: false,
                  color: LINE_BLUE,
                  font: { weight: 600, size: 10 },
                  formatter: (value: number) => formatDataLabelCompact(Number(value)),
                },
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 16, right: 8, bottom: 4 } },
            interaction: { mode: 'nearest', intersect: true },
            onHover: (event, elements, chart) => {
              const canvas = chart.canvas;
              if (!elements.length) {
                canvas.style.cursor = 'default';
                return;
              }
              const week = volumeByWeek[elements[0].index];
              canvas.style.cursor = onDrill && week && week.request > 0 ? 'pointer' : 'default';
            },
            plugins: {
              legend: { display: false },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: {
                  color: '#64748b',
                  maxRotation: 90,
                  minRotation: 0,
                  autoSkip: true,
                  maxTicksLimit: 32,
                },
              },
              y: {
                beginAtZero: true,
                suggestedMax: maxVol * 1.12,
                grid: { color: 'rgba(148, 163, 184, 0.35)' },
                border: { display: false },
                ticks: {
                  color: '#94a3b8',
                  callback: (v) => formatAxisCompactK(v as number),
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiRequestsWeeklyLineChart;
